import { __rest } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { makeStyles, Typography, useTheme } from '@material-ui/core';
import { c2foThemeFallbackFonts } from '../../../themes/C2FO';
import { InterFontNames } from '../../../themes/fonts';
import { liquidityThemeFallbackFonts } from '../../../themes/Liquidity';
const smallTextVariants = {
    body1: {
        size: 16,
        weight: { normalWeight: 400, emphasisWeight: 600, serifEmphasisWeight: null },
        lineHeight: '150%',
    },
    body2: {
        size: 14,
        weight: { normalWeight: 400, emphasisWeight: 600, serifEmphasisWeight: null },
        lineHeight: '143%',
    },
    subtitle1: {
        size: 16,
        weight: { normalWeight: 400, emphasisWeight: 600, serifEmphasisWeight: null },
        lineHeight: '175%',
    },
    subtitle2: {
        size: 14,
        weight: { normalWeight: 500, emphasisWeight: 800, serifEmphasisWeight: null },
        lineHeight: '157%',
    },
    caption: {
        size: 13,
        weight: { normalWeight: 400, emphasisWeight: 600, serifEmphasisWeight: null },
        lineHeight: '166%',
    },
    overline: {
        size: 13,
        weight: { normalWeight: 400, emphasisWeight: 600, serifEmphasisWeight: null },
        lineHeight: '150%',
    },
    legalese: {
        size: 12,
        weight: { normalWeight: 400, emphasisWeight: 600, serifEmphasisWeight: null },
        lineHeight: '130%',
    },
};
const heroStyles = {
    weight: { normalWeight: 400, emphasisWeight: 500, serifEmphasisWeight: 700 },
    lineHeight: '120%',
};
const h1Styles = {
    weight: { normalWeight: 400, emphasisWeight: 500, serifEmphasisWeight: 700 },
    lineHeight: '120%',
};
const h2Styles = {
    weight: { normalWeight: 400, emphasisWeight: 500, serifEmphasisWeight: 700 },
    lineHeight: '120%',
};
const h3Styles = {
    weight: { normalWeight: 500, emphasisWeight: 600, serifEmphasisWeight: 700 },
    lineHeight: '120%',
};
const h4Styles = {
    weight: { normalWeight: 400, emphasisWeight: 500, serifEmphasisWeight: 700 },
    lineHeight: '120%',
};
const desktopFont = Object.assign({ hero: Object.assign({ size: 52 }, heroStyles), h1: Object.assign({ size: 43 }, h1Styles), h2: Object.assign({ size: 35 }, h2Styles), h3: Object.assign({ size: 29 }, h3Styles), h4: Object.assign({ size: 24 }, h4Styles), h5: {
        size: 19,
        weight: { normalWeight: 500, emphasisWeight: 700, serifEmphasisWeight: null },
        lineHeight: '145%',
    } }, smallTextVariants);
const setDesktopFont = (variant) => {
    return desktopFont[variant];
};
const tabletFont = Object.assign({ hero: Object.assign({ size: 48 }, heroStyles), h1: Object.assign({ size: 40 }, h1Styles), h2: Object.assign({ size: 33 }, h2Styles), h3: Object.assign({ size: 26 }, h3Styles), h4: Object.assign({ size: 22 }, h4Styles), h5: {
        size: 18,
        weight: { normalWeight: 600, emphasisWeight: 700, serifEmphasisWeight: null },
        lineHeight: '145%',
    } }, smallTextVariants);
const setTabletFont = (variant) => {
    return tabletFont[variant];
};
const mobileFont = Object.assign({ hero: Object.assign({ size: 38 }, heroStyles), h1: Object.assign({ size: 29 }, h1Styles), h2: Object.assign({ size: 26 }, h2Styles), h3: Object.assign({ size: 23 }, h3Styles), h4: Object.assign({ size: 20 }, h4Styles), h5: {
        size: 17,
        weight: { normalWeight: 600, emphasisWeight: 700, serifEmphasisWeight: null },
        lineHeight: '145%',
    } }, smallTextVariants);
const setMobileFont = (variant) => {
    return mobileFont[variant];
};
const useStyles = makeStyles(({ breakpoints, typography: { pxToRem } }) => ({
    root: {
        color: ({ color }) => color,
        fontFamily: ({ fontFamily }) => fontFamily || [InterFontNames.normal, ...c2foThemeFallbackFonts].join(', '),
        '@supports (font-variation-settings: normal)': {
            fontFamily: ({ fontFamily }) => fontFamily || [InterFontNames.var, ...c2foThemeFallbackFonts].join(', '),
        },
        lineHeight: ({ variant }) => setDesktopFont(variant).lineHeight,
        textTransform: ({ variant }) => (variant === 'overline' ? 'uppercase' : 'none'),
        fontSize: ({ variant }) => pxToRem(setDesktopFont(variant).size),
        fontWeight: ({ variant, isEmphasis, fontType }) => {
            var _a;
            if (isEmphasis) {
                if (fontType === 'serif') {
                    return (_a = setDesktopFont(variant).weight.serifEmphasisWeight) !== null && _a !== void 0 ? _a : setDesktopFont(variant).weight.emphasisWeight;
                }
                return setDesktopFont(variant).weight.emphasisWeight;
            }
            return setDesktopFont(variant).weight.normalWeight;
        },
        [breakpoints.down('md')]: {
            fontSize: ({ variant }) => pxToRem(setTabletFont(variant).size),
            fontWeight: ({ variant, isEmphasis, fontType }) => {
                var _a;
                if (isEmphasis) {
                    if (fontType === 'serif') {
                        return (_a = setTabletFont(variant).weight.serifEmphasisWeight) !== null && _a !== void 0 ? _a : setTabletFont(variant).weight.emphasisWeight;
                    }
                    return setTabletFont(variant).weight.emphasisWeight;
                }
                return setTabletFont(variant).weight.normalWeight;
            },
        },
        [breakpoints.down('xs')]: {
            fontSize: ({ variant }) => pxToRem(setMobileFont(variant).size),
            fontWeight: ({ variant, isEmphasis, fontType }) => {
                var _a;
                if (isEmphasis) {
                    if (fontType === 'serif') {
                        return (_a = setMobileFont(variant).weight.serifEmphasisWeight) !== null && _a !== void 0 ? _a : setMobileFont(variant).weight.emphasisWeight;
                    }
                    return setMobileFont(variant).weight.emphasisWeight;
                }
                return setMobileFont(variant).weight.normalWeight;
            },
        },
    },
}));
const mapColor = (color) => {
    const { palette } = useTheme();
    switch (color) {
        case 'primary':
            return palette.text.primary;
        case 'secondary':
            return palette.text.secondary;
        case 'disabled':
            return palette.text.disabled;
        case 'hint':
            return palette.text.hint;
        case 'inverse':
            return palette.text.inverse;
    }
};
const setHtmlComponent = (component, variant) => {
    if (component) {
        return component;
    }
    if (['h1', 'h2', 'h3', 'h4', 'h5'].includes(variant)) {
        return variant;
    }
    if (variant === 'hero') {
        return 'h1';
    }
    return 'p';
};
const setFontFamily = (variant, fontType) => {
    const { name } = useTheme();
    return name === 'liquidity' && ['hero', 'h1', 'h2', 'h3', 'h4'].includes(variant) && fontType === 'serif'
        ? ['Libre Baskerville', 'serif', ...liquidityThemeFallbackFonts].join(',')
        : ['Roboto', ...liquidityThemeFallbackFonts].join(', ');
};
export const TypographySystem = (props) => {
    const { fontType = 'sans', variant = 'body1', customColor, color = 'primary', isEmphasis, component, children } = props, rest = __rest(props, ["fontType", "variant", "customColor", "color", "isEmphasis", "component", "children"]);
    const textColor = customColor || mapColor(color);
    const font = variant;
    const classes = useStyles({
        variant: font,
        fontFamily: setFontFamily(variant, fontType),
        color: textColor,
        isEmphasis,
        fontType,
    });
    return (_jsx(Typography, Object.assign({ component: setHtmlComponent(component, variant), className: classes.root }, rest, { children: children }), void 0));
};
//# sourceMappingURL=TypographySystem.js.map